import type { Workspace } from '@sunstone/api-client/gen/deployments/v1/deployments_pb';
import { useListWorkspaces } from '@sunstone/api-client/hooks';
import {
  Badge,
  Button,
  DataTable,
  PillFilter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@sunstone/ui';
import type { ColumnDef } from '@tanstack/react-table';
import { ChevronRight } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { PageHeader } from '@/components/ui/PageHeader';
import { PromoteWorkspacesDialog } from '@/components/ui/PromoteWorkspacesDialog';
import { ReleaseCombobox } from '@/components/ui/ReleaseCombobox';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { useAuthToken } from '@/features/auth';
import { useAuthorization } from '@/features/auth/useAuthorization';
import { getBulkPromoteEligibility } from '@/features/workspaces/bulkPromoteEligibility';
import {
  ENV_LABELS,
  STATUS_FILTER_OPTIONS,
  workspaceEnvironmentFromKey,
  workspaceEnvironmentKey,
  workspaceStatusFromKey,
  workspaceStatusKey,
} from '@/features/workspaces/utils';
import { VERSION_UNKNOWN_LABEL } from '@/features/workspaces/WorkspaceVersionStatus';
import { useDebounce } from '@/hooks/useDebounce';
import { useListState } from '@/hooks/useListState';
import { routes } from '@/lib/routes';

const PAGE_SIZE = 100;

const ALL = '__all__';

const ENV_FILTER_OPTIONS = Object.entries(ENV_LABELS).map(([value, label]) => ({ value, label }));

const ENV_BADGE_CLASSNAMES: Record<string, string> = {
  WORKSPACE_ENVIRONMENT_DEVELOPMENT: 'bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-300',
  WORKSPACE_ENVIRONMENT_STAGING:
    'bg-purple-50 text-purple-700 dark:bg-purple-950 dark:text-purple-300',
  WORKSPACE_ENVIRONMENT_PRODUCTION:
    'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
};

const ENV_BADGE_CFG: Record<string, { label: string; className: string }> = Object.fromEntries(
  Object.entries(ENV_LABELS).map(([key, label]) => [
    key,
    { label, className: ENV_BADGE_CLASSNAMES[key] ?? '' },
  ]),
);

export function WorkspacesPage() {
  const navigate = useNavigate();
  const { token } = useAuthToken();
  const { scope, hasPermission } = useAuthorization();
  const showsTenantColumn = scope !== 'tenant';

  const { page, search, setPage, setSearch, getFilter, setFilter } = useListState(true);
  const envFilter = getFilter('env');
  const statusFilter = getFilter('status');
  const releaseIdFilter = getFilter('releaseId');

  const handleEnvFilterChange = useCallback((v: string | null) => setFilter('env', v), [setFilter]);
  const handleStatusFilterChange = useCallback(
    (v: string | null) => setFilter('status', v),
    [setFilter],
  );
  const handleReleaseFilterChange = useCallback(
    (v: string | null) => setFilter('releaseId', v),
    [setFilter],
  );
  const handleSearchChange = useCallback((v: string) => setSearch(v), [setSearch]);

  const debouncedSearch = useDebounce(search);

  const {
    data: workspacesData,
    isLoading,
    error,
  } = useListWorkspaces(undefined, token, {
    search: debouncedSearch,
    page,
    pageSize: PAGE_SIZE,
    environmentFilter: workspaceEnvironmentFromKey(envFilter),
    statusFilter: workspaceStatusFromKey(statusFilter),
    releaseIdFilter: releaseIdFilter ?? undefined,
  });

  const workspaces = workspacesData?.workspaces ?? [];
  const totalCount = workspacesData?.totalCount ?? 0;

  const canBulkPromote = hasPermission('promote:workspace') || hasPermission('upgrade:workspace');
  const [selected, setSelected] = useState<Map<string, Workspace>>(new Map());
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);

  const handleRowSelectionChange = useCallback(
    (next: Record<string, boolean>) => {
      setSelected((prev) => {
        const updated = new Map(prev);
        for (const w of workspaces) {
          if (next[w.id]) {
            updated.set(w.id, w);
          } else {
            updated.delete(w.id);
          }
        }
        return updated;
      });
    },
    [workspaces],
  );

  const isRowSelectable = useCallback(
    (w: Workspace) => {
      const result = getBulkPromoteEligibility(
        w,
        [...selected.values()].filter((s) => s.id !== w.id),
      );
      return { selectable: result.eligible, reason: result.reason };
    },
    [selected],
  );

  const rowSelection = useMemo(
    () => Object.fromEntries([...selected.keys()].map((id) => [id, true])),
    [selected],
  );

  const columns = useMemo<ColumnDef<Workspace>[]>(
    () => [
      {
        id: 'displayName',
        accessorKey: 'displayName',
        header: 'Name',
        cell: ({ row }) => (
          <span className="font-medium text-foreground">{row.original.displayName}</span>
        ),
      },
      ...(showsTenantColumn
        ? [
            {
              id: 'tenant',
              header: 'Customer',
              cell: ({ row }: { row: { original: Workspace } }) => (
                <span className="text-sm text-muted-foreground hidden sm:block">
                  {row.original.tenantName || '—'}
                </span>
              ),
            } as ColumnDef<Workspace>,
          ]
        : []),
      {
        id: 'deployment',
        header: 'Deployment',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground hidden sm:block">
            {row.original.deploymentDisplayName || '—'}
          </span>
        ),
      },
      {
        id: 'infra',
        header: 'Infra',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground font-mono hidden sm:block">
            {row.original.infra || '—'}
          </span>
        ),
      },
      {
        id: 'environment',
        header: 'Environment',
        cell: ({ row }) => {
          const envKey = workspaceEnvironmentKey(row.original.environment);
          const cfg = ENV_BADGE_CFG[envKey] ?? {
            label: ENV_LABELS[envKey] ?? envKey,
            className: '',
          };
          return <Badge className={`text-xs ${cfg.className}`}>{cfg.label}</Badge>;
        },
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => <StatusBadge status={workspaceStatusKey(row.original.status)} />,
      },
      {
        id: 'version',
        header: 'Version',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground font-mono hidden md:block">
            {row.original.currentVersion || VERSION_UNKNOWN_LABEL}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground/40 hover:text-muted-foreground"
            onClick={() =>
              navigate(
                `/tenants/${row.original.tenantId}/deployments/${row.original.deploymentId}/workspaces/${row.original.id}`,
              )
            }
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        ),
      },
    ],
    [showsTenantColumn, navigate],
  );

  if (error) {
    return (
      <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <PageHeader
          title="Workspaces"
          description="Browse and manage workspaces across all deployments."
        />
        <div className="flex items-center justify-center p-8 text-destructive">
          Failed to load workspaces.
        </div>
      </div>
    );
  }

  const filterSlot = (
    <>
      <PillFilter
        label="Environment"
        value={envFilter}
        onChange={handleEnvFilterChange}
        options={ENV_FILTER_OPTIONS}
      />
      <div className="flex items-center gap-1.5">
        <span className="text-xs text-muted-foreground font-medium">Status:</span>
        <Select
          value={statusFilter ?? ALL}
          onValueChange={(v) => handleStatusFilterChange(v === ALL ? null : v)}
        >
          <SelectTrigger className="h-7 w-40 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Any status</SelectItem>
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-xs text-muted-foreground font-medium">Release:</span>
        <ReleaseCombobox
          token={token}
          value={releaseIdFilter ?? ''}
          onChange={(v) => handleReleaseFilterChange(v || null)}
          deployableOnly={false}
          allowClear
          placeholder="Any release"
          className="h-7 w-40 text-xs"
        />
      </div>
    </>
  );

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <PageHeader
        title="Workspaces"
        description="Browse and manage workspaces across all deployments."
        action={
          selected.size > 0 ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{selected.size} selected</span>
              <Button variant="outline" size="sm" onClick={() => setSelected(new Map())}>
                Clear
              </Button>
              <Button size="sm" onClick={() => setPromoteDialogOpen(true)}>
                Promote / Upgrade
              </Button>
            </div>
          ) : (
            hasPermission('create:workspace') && (
              <Button size="sm" onClick={() => navigate(routes.newWorkspaceGlobal())}>
                New Workspace
              </Button>
            )
          )
        }
      />
      <DataTable
        columns={columns}
        data={workspaces}
        onRowClick={(row) =>
          navigate(`/tenants/${row.tenantId}/deployments/${row.deploymentId}/workspaces/${row.id}`)
        }
        showSearch
        searchPlaceholder="Search"
        searchValue={search}
        onSearchChange={handleSearchChange}
        filterSlot={filterSlot}
        isLoading={isLoading}
        totalCount={totalCount}
        currentPage={page}
        onPageChange={setPage}
        pageSize={PAGE_SIZE}
        enableRowSelection={canBulkPromote}
        getRowId={(w) => w.id}
        rowSelection={rowSelection}
        onRowSelectionChange={handleRowSelectionChange}
        isRowSelectable={isRowSelectable}
      />
      <PromoteWorkspacesDialog
        open={promoteDialogOpen}
        onOpenChange={setPromoteDialogOpen}
        workspaces={[...selected.values()]}
        onSuccess={() => setSelected(new Map())}
      />
    </div>
  );
}
